import React from 'react';
import { API } from '../api';
import { DataRow, DataRowHeader, Field, Section, Segmented, Toggle } from './ui';

/* ====================================================================
   MAILING LIST — who the club's news goes to, members or not.

   What an admin can and cannot do here is the point of the design:
   - Add someone (the paper sheet on the noticeboard).
   - Mark someone unsubscribed (they asked in person).
   - Approve or decline a request to delete someone's details, one at a time.
   - NOT put back anybody who unsubscribed. Only they can, from the link in
     an email, or the list would stop meaning anything.
   There is no bulk delete, on purpose.
   ==================================================================== */

const STATUS_OPTIONS = [
  { value: 'subscribed', label: 'Subscribed' },
  { value: 'pending', label: 'Awaiting confirmation' },
  { value: 'unsubscribed', label: 'Unsubscribed' },
];

const SOURCE_LABEL = { public: 'Signed up', member: 'Member', admin: 'Added by club' };

const fmt = d => (d ? new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '');

/* The things a person ticks when they subscribe. Renaming keeps the id, so
   everyone who ticked "Bowls events" still gets it when it becomes
   "Tournaments". Removing one means nobody can choose it any more; people
   who had it keep their other choices. */
function TopicsEditor({ topics, busy, onSave }) {
  const [draft, setDraft] = React.useState(topics);
  React.useEffect(() => { setDraft(topics); }, [topics]);
  const changed = JSON.stringify(draft.map(t => [t.id, t.label])) !== JSON.stringify(topics.map(t => [t.id, t.label]));
  const set = (i, label) => setDraft(d => d.map((t, j) => (j === i ? { ...t, label } : t)));

  return (
    <>
      <p className="w3-text-grey" style={{ marginTop: 0 }}>
        People tick these when they subscribe, and you can send a newsletter to just one of them.
        The number is how many are interested.
      </p>
      {/* A row of words rather than a stack of full-width text boxes. There
          are at most eight of these and they are changed about once a season,
          so a form the size of the list it configures was the wrong shape.
        *
          The word is still an input — renaming is the whole reason this is
          editable at all, because a rename keeps the id and everyone who
          ticked "Bowls events" still gets it once it says "Tournaments". It
          just does not look like a form until you click into it, and it is
          sized to its own text so a two-word topic does not reserve 420px. */}
      <div className="ml-topic-row">
        {draft.map((t, i) => {
          const live = t.id && topics.find(x => x.id === t.id);
          return (
            <span className="ml-topic" key={t.id || `new${i}`}>
              <input className="ml-topic-name" maxLength={60}
                size={Math.max(8, t.label.length + 1)}
                placeholder="Name it"
                aria-label={`Choice ${i + 1}`} value={t.label} onChange={e => set(i, e.target.value)} />
              {live ? <span className="ml-topic-count">{t.interested}</span> : null}
              <button type="button" className="ml-topic-bin" disabled={draft.length <= 1}
                aria-label={t.label ? `Remove "${t.label}"` : 'Remove this choice'}
                title={draft.length <= 1 ? 'Keep at least one choice' : 'Remove'}
                onClick={() => setDraft(d => d.filter((_, j) => j !== i))}>
                <i className="fa fa-trash-o" aria-hidden="true"></i>
              </button>
            </span>
          );
        })}
        <button type="button" className="w3-button w3-round poster-btn ml-topic-add" disabled={draft.length >= 8}
          onClick={() => setDraft(d => [...d, { id: '', label: '' }])}>
          <i className="fa fa-plus w3-margin-right"></i>Add a choice
        </button>
        {/* Only once there is something to save. It used to sit there on every
            visit looking like an action somebody had forgotten to take. */}
        {changed && (
          <button type="button" className="w3-button w3-round poster-btn-primary"
            disabled={busy || !draft.some(t => t.label.trim())}
            onClick={() => onSave(draft)}>
            {busy ? <i className="fa fa-spinner fa-spin"></i> : 'Save choices'}
          </button>
        )}
      </div>
    </>
  );
}

function MailingListTab({ clubId, showToast, showConfirm }) {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState('');
  const [status, setStatus] = React.useState('subscribed');
  const [query, setQuery] = React.useState('');
  const [addName, setAddName] = React.useState('');
  const [addEmail, setAddEmail] = React.useState('');
  const [busy, setBusy] = React.useState(null);
  // The add-someone form, off until asked for.
  const [adding, setAdding] = React.useState(false);
  // What the person being added wants. All ticked to start; untick what they said no to.
  const [addInterests, setAddInterests] = React.useState([]);
  const topicKey = data ? data.topics.map(t => t.id).join(',') : '';
  React.useEffect(() => {
    if (data) setAddInterests(data.topics.map(t => t.id));
  }, [topicKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const qs =clubId ? `?clubId=${encodeURIComponent(clubId)}` : '';

  const load = React.useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/mailing-list${qs}`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Could not load the mailing list.');
      setData(d);
      setError('');
    } catch (e) {
      setError(e.message);
    }
  }, [qs]);

  React.useEffect(() => { load(); }, [load]);

  const call = async (key, url, body) => {
    setBusy(key);
    try {
      const r = await fetch(`${API}${url}`, {
        method: url.endsWith('/settings') ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clubId, ...(body || {}) }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'That did not work.');
      await load();
      return d;
    } catch (e) {
      showToast && showToast(e.message, 'error');
      return null;
    } finally {
      setBusy(null);
    }
  };

  const add = async (e) => {
    e.preventDefault();
    if (!addInterests.length) { showToast && showToast('Tick at least one thing they want to hear about.', 'error'); return; }
    const d = await call('add', '/api/mailing-list/contacts', { name: addName, email: addEmail, interests: addInterests });
    if (d) {
      setAddName(''); setAddEmail(''); setAddInterests(data.topics.map(t => t.id));
      /* Closed again, because adding one person off the sign-up sheet is the
         usual case and the new row is in the list underneath. Somebody with
         three to enter opens it three times, which is cheaper than everybody
         else scrolling past an open form. */
      setAdding(false);
      showToast && showToast('Added to the mailing list');
    }
  };

  const unsubscribe = async (c) => {
    const ok = await showConfirm(`Unsubscribe ${c.email}?\n\nThey will stop getting newsletters and club-wide emails. Only they can sign up again.`);
    if (ok) await call(`u${c.id}`, `/api/mailing-list/contacts/${c.id}/unsubscribe`);
  };

  const erase = async (c) => {
    const ok = await showConfirm(`Delete ${c.email} from the mailing list?\n\n`
      + 'Their name and address are removed. The club keeps a one-way fingerprint of the address so it is never emailed again by mistake.'
      + (c.isMember ? '\n\nThey are also a member. Their member record is not changed. Edit it on the Members tab if needed.' : ''));
    if (ok && await call(`e${c.id}`, `/api/mailing-list/contacts/${c.id}/erase`)) showToast && showToast('Details deleted');
  };

  /* One box ticked or unticked on someone's row. Null interests is
     "everything", so it starts from every topic. */
  const toggleInterest = async (c, topicId, on) => {
    const current = c.interests || data.topics.map(t => t.id);
    const next = on ? [...new Set([...current, topicId])] : current.filter(id => id !== topicId);
    const live = next.filter(id => data.topics.some(t => t.id === id));
    if (!live.length) {
      const ok = await showConfirm(`That's the last thing ${c.name || c.email} gets.\n\nUnticking it unsubscribes them from everything, and only they can sign up again.`);
      if (!ok) return;
    }
    await call(`i${c.id}`, `/api/mailing-list/contacts/${c.id}/interests`, { interests: live });
  };

  const resubscribe = async (c) => {
    const byClub = c.unsubscribedBy === 'admin';
    const ok = await showConfirm(`Put ${c.name || c.email} back on the list?\n\n`
      + 'They go back with the same choices as before.'
      + (byClub ? '' : '\n\nThey unsubscribed themselves. Only do this if they asked you to, as the club needs their permission to email them again.'));
    if (!ok) return;
    const d = await call(`r${c.id}`, `/api/mailing-list/contacts/${c.id}/resubscribe`);
    if (d) showToast && showToast('Back on the list');
  };

  const keep = async (c) => {
    if (await call(`k${c.id}`, `/api/mailing-list/contacts/${c.id}/keep`)) showToast && showToast('Request declined. They stay unsubscribed.');
  };

  if (!data) {
    return error
      ? <div className="w3-panel w3-pale-red w3-leftbar w3-border-red"><p>{error}</p></div>
      : <p className="w3-text-grey">Loading the mailing list…</p>;
  }

  const requests = data.contacts.filter(c => c.deletionRequestedAt);
  const q = query.trim().toLowerCase();
  const shown = data.contacts
    .filter(c => c.status === status)
    .filter(c => !q || (c.email || '').includes(q) || (c.name || '').toLowerCase().includes(q));

  const columns = [
    { key: 'name', label: 'Name', grow: true, primary: true, minWidth: 160, render: c => c.name || <span className="w3-text-grey">—</span> },
    { key: 'email', label: 'Email', grow: true, minWidth: 200, render: c => <span style={{ wordBreak: 'break-all' }}>{c.email}</span> },
    ...(status !== 'unsubscribed' ? [{
      key: 'interests', label: 'Interested in', grow: true, minWidth: 200, collapse: false,
      render: c => (
        <span style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px' }}>
          {data.topics.map(t => (
            <label key={t.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', minHeight: 32 }}>
              <input type="checkbox" className="w3-check" style={{ top: 0, width: 18, height: 18 }}
                checked={!c.interests || c.interests.includes(t.id)}
                disabled={!!busy}
                aria-label={`${c.name || c.email}: ${t.label}`}
                onChange={e => toggleInterest(c, t.id, e.target.checked)} />
              <span>{t.label}</span>
            </label>
          ))}
        </span>
      ),
    }] : []),
    { key: 'source', label: 'How', width: 120, render: c => <span className="w3-text-grey">{c.isMember ? 'Member' : (SOURCE_LABEL[c.source] || c.source || '')}</span> },
    {
      key: 'when',
      label: status === 'unsubscribed' ? 'Left' : status === 'pending' ? 'Asked' : 'Since',
      width: 120,
      render: c => fmt(status === 'unsubscribed' ? c.unsubscribedAt : status === 'pending' ? c.createdAt : (c.confirmedAt || c.createdAt)),
    },
    ...(status === 'unsubscribed' ? [{
      key: 'reason', label: 'Reason', grow: true, minWidth: 160,
      render: c => (
        <span className="w3-text-grey">
          {c.unsubscribedBy === 'admin' ? 'By the club' : 'By them'}
          {c.unsubscribeReason ? ` · ${c.unsubscribeReason}` : ''}
        </span>
      ),
    }] : []),
    {
      key: 'act', width: status === 'unsubscribed' ? 260 : 130, align: 'right',
      render: c => (status === 'unsubscribed' ? (
        <span style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {!c.deletionRequestedAt && (
            <button className="w3-button w3-round poster-btn" disabled={!!busy} onClick={() => resubscribe(c)}>
              Re-subscribe
            </button>
          )}
          <button className="w3-button w3-round poster-btn" disabled={!!busy} onClick={() => erase(c)}>Delete details</button>
        </span>
      ) : (
        <button className="w3-button w3-round poster-btn" disabled={!!busy} onClick={() => unsubscribe(c)}>Unsubscribe</button>
      )),
    },
  ];

  const requestColumns = [
    { key: 'email', label: 'Email', grow: true, primary: true, minWidth: 200, render: c => <><strong style={{ wordBreak: 'break-all' }}>{c.email}</strong>{c.name ? <span className="w3-text-grey"> · {c.name}</span> : null}{c.isMember ? <span className="gs-chip" style={{ marginLeft: 8 }}>Member</span> : null}</> },
    { key: 'asked', label: 'Asked', width: 120, render: c => fmt(c.deletionRequestedAt) },
    { key: 'reason', label: 'Reason', grow: true, minWidth: 160, render: c => <span className="w3-text-grey">{c.unsubscribeReason || ''}</span> },
    {
      key: 'act', width: 220, align: 'right',
      render: c => (
        <span style={{ display: 'inline-flex', gap: 8 }}>
          <button className="w3-button w3-round poster-btn" disabled={!!busy} onClick={() => keep(c)}>Decline</button>
          <button className="w3-button w3-round poster-btn-primary" disabled={!!busy} onClick={() => erase(c)}>Approve delete</button>
        </span>
      ),
    },
  ];

  return (
    <div className="mailing-list-tab">
      {error && <div className="w3-panel w3-pale-red w3-leftbar w3-border-red"><p>{error}</p></div>}

      {requests.length > 0 && (
        <Section label={`Deletion requests (${requests.length})`}>
          <p className="w3-text-grey" style={{ marginTop: 0 }}>
            These people unsubscribed and asked for their details to be deleted. They are already off the list;
            nothing is deleted until you approve it.
          </p>
          <DataRowHeader columns={requestColumns} />
          {requests.map(c => <DataRow key={c.id} columns={requestColumns} item={c} />)}
        </Section>
      )}

      <Section label="Sign-ups">
        <Toggle label="Let people sign up from the login page and newsletter links"
          checked={!!data.acceptsPublicSubscriptions}
          disabled={busy === 'settings'}
          onChange={v => call('settings', '/api/mailing-list/settings', { acceptsPublicSubscriptions: v })} />
        <p className="w3-text-grey" style={{ marginBottom: 0 }}>
          Anyone signing up gets an email and must click the link in it before they are added.
          Unsubscribe links in emails always work, whatever this is set to.
        </p>
      </Section>

      <Section label="What people can choose">
        <TopicsEditor topics={data.topics} busy={busy === 'topics'}
          onSave={async topics => {
            if (await call('topics', '/api/mailing-list/settings', { topics })) showToast && showToast('Saved');
          }} />
      </Section>

      {/* Adding somebody by hand is the rare case — the sign-up sheet off the
          noticeboard, a few times a season. It had a section of its own,
          permanently open, pushing the list it adds to below the fold. Now it
          is a button on the list's own heading, which is where it belongs:
          the thing it does is put a row in that list. */}
      <Section label="The list" action={
        <button type="button" className="w3-button w3-round poster-btn"
          aria-expanded={adding}
          onClick={() => setAdding(v => !v)}>
          <i className={`fa ${adding ? 'fa-times' : 'fa-plus'} w3-margin-right`}></i>
          {adding ? 'Cancel' : 'Add someone'}
        </button>
      }>
        {adding && (
          <form onSubmit={add} className="ml-add-form">
            <div className="gs-field-grid" style={{ '--gs-cols': 'repeat(auto-fit, minmax(200px, 1fr))' }}>
              <Field label="Name" htmlFor="ml-add-name">
                <input id="ml-add-name" className="w3-input" value={addName} maxLength={120}
                  /* The form is not on the page until it is asked for, so the
                     cursor goes where it was asked for. */
                  autoFocus
                  onChange={e => setAddName(e.target.value)} />
              </Field>
              <Field label="Email" htmlFor="ml-add-email" help="Only add people who have asked, e.g. on the clubhouse sign-up sheet.">
                <input id="ml-add-email" className="w3-input" type="email" required value={addEmail} onChange={e => setAddEmail(e.target.value)} />
              </Field>
            </div>
            <fieldset style={{ border: 'none', padding: 0, margin: '0 0 12px' }}>
              <legend className="gs-field-label" style={{ padding: 0 }}>Interested in</legend>
              <span style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 20px' }}>
                {data.topics.map(t => (
                  <label key={t.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', minHeight: 36 }}>
                    <input type="checkbox" className="w3-check" style={{ top: 0, width: 18, height: 18 }}
                      checked={addInterests.includes(t.id)}
                      onChange={e => setAddInterests(ids => (e.target.checked ? [...ids, t.id] : ids.filter(x => x !== t.id)))} />
                    <span>{t.label}</span>
                  </label>
                ))}
              </span>
            </fieldset>
            <button className="w3-button w3-round poster-btn-primary" type="submit" disabled={busy === 'add'}>
              {busy === 'add' ? <i className="fa fa-spinner fa-spin"></i> : <><i className="fa fa-plus w3-margin-right"></i>Add to list</>}
            </button>
          </form>
        )}

        <p className="w3-text-grey" style={{ marginTop: 0 }}>
          Every member with an email address is on the list automatically, and comes off it if they're deleted.
          Members can change what they get from their own profile.
          People who aren't members get newsletters sent to the whole club, but not ones filtered by membership type.
        </p>
        <div className="gs-field-grid" style={{ '--gs-cols': 'auto minmax(0, 1fr)', alignItems: 'end' }}>
          <Segmented ariaLabel="Show" name="ml-status" value={status} onChange={setStatus}
            options={STATUS_OPTIONS.map(o => ({ ...o, label: `${o.label} (${data.counts[o.value] || 0})` }))} />
          <Field label="Search" htmlFor="ml-search">
            <input id="ml-search" className="w3-input" value={query} onChange={e => setQuery(e.target.value)} placeholder="Name or email" />
          </Field>
        </div>
        {shown.length === 0 ? (
          <p className="w3-text-grey">{q ? 'Nobody matches that.' : 'Nobody here yet.'}</p>
        ) : (
          <>
            <DataRowHeader columns={columns} />
            {shown.map(c => <DataRow key={c.id} columns={columns} item={c} />)}
          </>
        )}
      </Section>
    </div>
  );
}

export default MailingListTab;
